import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const WarrantyClaimsDashboard: React.FC = () => {
  const warrantyKPIs = [{
    title: 'Warranty Cost per 1,000 units',
    value: '₹2,685',
    change: '+8.2%',
    isPositive: false
  }, {
    title: 'Total Claims',
    value: '3,563',
    change: '+12.5%',
    isPositive: false
  }, {
    title: 'Repeat Failure %',
    value: '14.8%',
    change: '+2.1%',
    isPositive: false
  }];
  const warrantyCostTrendData = [{
    month: 'Jan',
    cost: 42.5,
    claims: 245,
    costPerUnit: 2420
  }, {
    month: 'Feb',
    cost: 48.2,
    claims: 268,
    costPerUnit: 2485
  }, {
    month: 'Mar',
    cost: 52.8,
    claims: 292,
    costPerUnit: 2540
  }, {
    month: 'Apr',
    cost: 49.5,
    claims: 278,
    costPerUnit: 2510
  }, {
    month: 'May',
    cost: 58.4,
    claims: 312,
    costPerUnit: 2620
  }, {
    month: 'Jun',
    cost: 51.2,
    claims: 285,
    costPerUnit: 2550
  }, {
    month: 'Jul',
    cost: 54.8,
    claims: 298,
    costPerUnit: 2590
  }, {
    month: 'Aug',
    cost: 62.5,
    claims: 325,
    costPerUnit: 2680
  }, {
    month: 'Sep',
    cost: 56.8,
    claims: 308,
    costPerUnit: 2610
  }, {
    month: 'Oct',
    cost: 53.2,
    claims: 295,
    costPerUnit: 2570
  }, {
    month: 'Nov',
    cost: 50.5,
    claims: 282,
    costPerUnit: 2540
  }, {
    month: 'Dec',
    cost: 48.8,
    claims: 275,
    costPerUnit: 2520
  }];
  const defectCategoriesData = [{
    category: 'Electrical',
    claims: 1245,
    cost: 218,
    percentage: 35,
    cumulative: 35
  }, {
    category: 'Mechanical',
    claims: 892,
    cost: 156,
    percentage: 25,
    cumulative: 60
  }, {
    category: 'Software',
    claims: 623,
    cost: 109,
    percentage: 17,
    cumulative: 77
  }, {
    category: 'Body & Trim',
    claims: 445,
    cost: 78,
    percentage: 13,
    cumulative: 90
  }, {
    category: 'HVAC',
    claims: 358,
    cost: 68,
    percentage: 10,
    cumulative: 100
  }];
  const stateClaimsData = [{
    state: 'Maharashtra',
    claims: 485,
    cost: 85.2,
    severity: 'high'
  }, {
    state: 'Delhi',
    claims: 425,
    cost: 74.5,
    severity: 'high'
  }, {
    state: 'Karnataka',
    claims: 398,
    cost: 69.8,
    severity: 'high'
  }, {
    state: 'Tamil Nadu',
    claims: 365,
    cost: 64.2,
    severity: 'medium'
  }, {
    state: 'Gujarat',
    claims: 342,
    cost: 60.1,
    severity: 'medium'
  }, {
    state: 'Uttar Pradesh',
    claims: 328,
    cost: 57.6,
    severity: 'medium'
  }, {
    state: 'West Bengal',
    claims: 285,
    cost: 50.1,
    severity: 'medium'
  }, {
    state: 'Telangana',
    claims: 268,
    cost: 47.2,
    severity: 'medium'
  }, {
    state: 'Rajasthan',
    claims: 245,
    cost: 43.1,
    severity: 'low'
  }, {
    state: 'Madhya Pradesh',
    claims: 228,
    cost: 40.1,
    severity: 'low'
  }, {
    state: 'Haryana',
    claims: 215,
    cost: 37.8,
    severity: 'low'
  }, {
    state: 'Punjab',
    claims: 198,
    cost: 34.8,
    severity: 'low'
  }, {
    state: 'Kerala',
    claims: 185,
    cost: 32.5,
    severity: 'low'
  }, {
    state: 'Odisha',
    claims: 142,
    cost: 25.0,
    severity: 'low'
  }, {
    state: 'Assam',
    claims: 125,
    cost: 22.0,
    severity: 'low'
  }];
  const highRiskSKUsData = [{
    sku: 'LED-HL-2024-M',
    product: 'LED Headlamp Module',
    claims: 485,
    cost: 85.2,
    repeatRate: 28.5,
    affectedModels: 'Swift, Baleno, Brezza',
    severity: 'Critical'
  }, {
    sku: 'BATT-SYS-2023-H',
    product: 'Battery Management System',
    claims: 425,
    cost: 74.5,
    repeatRate: 24.8,
    affectedModels: 'Nexon EV, Tiago EV',
    severity: 'Critical'
  }, {
    sku: 'SENS-MOD-2024-K',
    product: 'Parking Sensor Module',
    claims: 398,
    cost: 69.8,
    repeatRate: 22.3,
    affectedModels: 'Creta, Venue, i20',
    severity: 'High'
  }, {
    sku: 'TAIL-LP-2023-T',
    product: 'Tail Lamp Assembly',
    claims: 365,
    cost: 64.2,
    repeatRate: 19.8,
    affectedModels: 'Harrier, Safari, Punch',
    severity: 'High'
  }, {
    sku: 'DOOR-HDL-2024-M',
    product: 'Door Handle Assembly',
    claims: 342,
    cost: 60.1,
    repeatRate: 18.5,
    affectedModels: 'XUV700, Scorpio N',
    severity: 'High'
  }, {
    sku: 'WIRE-HRN-2023-K',
    product: 'Wiring Harness',
    claims: 328,
    cost: 57.6,
    repeatRate: 16.2,
    affectedModels: 'Seltos, Sonet, Carens',
    severity: 'Medium'
  }, {
    sku: 'INT-TRM-2024-H',
    product: 'Interior Trim Panel',
    claims: 285,
    cost: 50.1,
    repeatRate: 15.8,
    affectedModels: 'City, Amaze, Elevate',
    severity: 'Medium'
  }, {
    sku: 'HVAC-BLW-2023-T',
    product: 'HVAC Blower Motor',
    claims: 268,
    cost: 47.2,
    repeatRate: 14.5,
    affectedModels: 'Innova, Fortuner',
    severity: 'Medium'
  }, {
    sku: 'BRAK-PAD-2024-V',
    product: 'Brake Pad Assembly',
    claims: 245,
    cost: 43.1,
    repeatRate: 12.8,
    affectedModels: 'Taigun, Virtus',
    severity: 'Medium'
  }, {
    sku: 'FUEL-PMP-2023-M',
    product: 'Fuel Pump Module',
    claims: 228,
    cost: 40.1,
    repeatRate: 11.5,
    affectedModels: 'Hector, Astor, ZS EV',
    severity: 'Low'
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
      case 'Critical':
        return 'bg-red-500';
      case 'medium':
      case 'High':
        return 'bg-orange-500';
      case 'Medium':
        return 'bg-yellow-500';
      default:
        return 'bg-green-500';
    }
  };
  const getSeverityTextColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'text-red-600 bg-red-50 border-red-300';
      case 'High':
        return 'text-orange-600 bg-orange-50 border-orange-300';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-300';
      default:
        return 'text-green-600 bg-green-50 border-green-300';
    }
  };
  return <div data-id="element-1000">
      <DescriptionPanel title="Warranty Claims Analysis" description="Detailed analysis of warranty claims including cost trends, defect categories, geographic distribution, and high-risk SKUs. Monitor warranty performance to identify quality issues and reduce warranty costs." insights={['Warranty cost per 1,000 units increased to ₹2,685 - 8.2% higher than target', 'Electrical defects account for 35% of all claims - top priority for quality improvement', 'Maharashtra, Delhi, and Karnataka represent 37% of total claims', 'Top 5 high-risk SKUs contribute to 47% of total warranty costs']} data-id="element-1001" />
      {/* Warranty KPI Row */}
      <div className="mb-8" data-id="element-1002">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1003">
          Warranty Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1004">
          {warrantyKPIs.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-1005">
              <div className="flex flex-col h-full" data-id="element-1006">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-1007">
                  {kpi.title}
                </div>
                <div className="mt-3 text-3xl font-bold text-gray-900" data-id="element-1008">
                  {kpi.value}
                </div>
                <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1009">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-1010" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-1011" />}
                  {kpi.change}
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Warranty Cost Trend */}
      <Card className="p-6 mb-8" data-id="element-1012">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1013">
          Warranty Cost Trend (12 Months)
        </h3>
        <ResponsiveContainer width="100%" height={350} data-id="element-1014">
          <ComposedChart data={warrantyCostTrendData} data-id="element-1015">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1016" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-1017" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} label={{
            value: 'Cost (₹ Lakhs)',
            angle: -90,
            position: 'insideLeft'
          }} data-id="element-1018" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 12
          }} label={{
            value: 'Claims Count',
            angle: 90,
            position: 'insideRight'
          }} data-id="element-1019" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-1020" />
            <Legend data-id="element-1021" />
            <Bar yAxisId="right" dataKey="claims" fill="#93c5fd" name="Claims Count" opacity={0.6} data-id="element-1022" />
            <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#ef4444" strokeWidth={3} name="Warranty Cost (₹ Lakhs)" dot={{
            r: 4
          }} data-id="element-1023" />
            <Line yAxisId="left" type="monotone" dataKey="costPerUnit" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="Cost per 1,000 units (₹)" dot={{
            r: 3
          }} data-id="element-1024" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1025">
          <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-1026">
            <div className="text-center" data-id="element-1027">
              <div className="text-gray-600" data-id="element-1028">Total Cost YTD</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1029">₹629 Lakhs</div>
            </div>
            <div className="text-center" data-id="element-1030">
              <div className="text-gray-600" data-id="element-1031">Total Claims YTD</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1032">3,563</div>
            </div>
            <div className="text-center" data-id="element-1033">
              <div className="text-gray-600" data-id="element-1034">Avg Cost per Claim</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-1035">₹17,658</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Defect Categories Pareto and State Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-1036">
        <Card className="p-6" data-id="element-1037">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1038">
            Top Defect Categories - Pareto Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1039">
            80/20 rule: Focus on top categories for maximum impact
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1040">
            <ComposedChart data={defectCategoriesData} data-id="element-1041">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1042" />
              <XAxis dataKey="category" tick={{
              fontSize: 11
            }} data-id="element-1043" />
              <YAxis yAxisId="left" tick={{
              fontSize: 12
            }} label={{
              value: 'Claims',
              angle: -90,
              position: 'insideLeft'
            }} data-id="element-1044" />
              <YAxis yAxisId="right" orientation="right" tick={{
              fontSize: 12
            }} domain={[0, 100]} label={{
              value: 'Cumulative %',
              angle: 90,
              position: 'insideRight'
            }} data-id="element-1045" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1046" />
              <Legend data-id="element-1047" />
              <Bar yAxisId="left" dataKey="claims" fill="#3b82f6" name="Claims Count" data-id="element-1048" />
              <Line yAxisId="right" type="monotone" dataKey="cumulative" stroke="#ef4444" strokeWidth={3} name="Cumulative %" dot={{
              r: 5
            }} data-id="element-1049" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1050">
            <div className="flex items-center justify-between text-sm" data-id="element-1051">
              <span className="text-gray-600" data-id="element-1052">Top 3 categories represent:</span>
              <span className="text-xl font-bold text-red-600" data-id="element-1053">
                77% of claims
              </span>
            </div>
          </div>
        </Card>
        <Card className="p-6" data-id="element-1054">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1055">
            State-wise Claims Heatmap
          </h3>
          <div className="space-y-2 max-h-[350px] overflow-y-auto" data-id="element-1056">
            {stateClaimsData.map((state, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1057">
                <div className="flex items-center flex-1" data-id="element-1058">
                  <div className={`w-3 h-3 rounded-full ${getSeverityColor(state.severity)} mr-3`} data-id="element-1059" />
                  <div className="flex-1" data-id="element-1060">
                    <div className="text-sm font-semibold text-gray-800" data-id="element-1061">
                      {state.state}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1062">
                      ₹{state.cost}L cost
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-1063">
                  <div className="text-sm font-bold text-gray-900" data-id="element-1064">
                    {state.claims}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-1065">claims</div>
                </div>
              </div>)}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1066">
            <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-1067">
              <span data-id="element-1068">Severity Levels:</span>
              <div className="flex items-center space-x-3" data-id="element-1069">
                <div className="flex items-center" data-id="element-1070">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-1071" />
                  <span data-id="element-1072">High (&gt;350)</span>
                </div>
                <div className="flex items-center" data-id="element-1073">
                  <div className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-1074" />
                  <span data-id="element-1075">Medium (200-350)</span>
                </div>
                <div className="flex items-center" data-id="element-1076">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-1" data-id="element-1077" />
                  <span data-id="element-1078">Low (&lt;200)</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* High-Risk SKUs Table */}
      <Card className="p-6" data-id="element-1079">
        <div className="flex items-center justify-between mb-4" data-id="element-1080">
          <div data-id="element-1081">
            <h3 className="text-lg font-semibold text-gray-800" data-id="element-1082">
              High-Risk SKUs & Claim Analysis
            </h3>
            <p className="text-sm text-gray-600" data-id="element-1083">
              Products with highest warranty impact - prioritize for quality
              improvement
            </p>
          </div>
          <div className="flex items-center" data-id="element-1084">
            <AlertTriangleIcon size={20} className="text-red-600 mr-2" data-id="element-1085" />
            <span className="text-sm font-semibold text-red-600" data-id="element-1086">
              10 Critical SKUs
            </span>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-1087">
          <table className="w-full" data-id="element-1088">
            <thead data-id="element-1089">
              <tr className="border-b-2 border-gray-300" data-id="element-1090">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1091">
                  SKU Code
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1092">
                  Product Description
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1093">
                  Total Claims
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1094">
                  Warranty Cost
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1095">
                  Repeat Rate %
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1096">
                  Affected Models
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1097">
                  Severity
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1098">
              {highRiskSKUsData.map((sku, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1099">
                  <td className="py-3 px-4 text-sm font-mono text-blue-600" data-id="element-1100">
                    {sku.sku}
                  </td>
                  <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-1101">
                    {sku.product}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-1102">
                    {sku.claims}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-red-600" data-id="element-1103">
                    ₹{sku.cost}L
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-orange-600" data-id="element-1104">
                    {sku.repeatRate}%
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1105">
                    {sku.affectedModels}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1106">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${getSeverityTextColor(sku.severity)}`} data-id="element-1107">
                      {sku.severity}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1108">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-1109">
            <div className="text-center" data-id="element-1110">
              <div className="text-gray-600" data-id="element-1111">Total SKUs Tracked</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1112">10</div>
            </div>
            <div className="text-center" data-id="element-1113">
              <div className="text-gray-600" data-id="element-1114">Total Claims</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1115">3,369</div>
            </div>
            <div className="text-center" data-id="element-1116">
              <div className="text-gray-600" data-id="element-1117">Total Cost</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1118">₹591.7L</div>
            </div>
            <div className="text-center" data-id="element-1119">
              <div className="text-gray-600" data-id="element-1120">Avg Repeat Rate</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-1121">18.5%</div>
            </div>
            <div className="text-center" data-id="element-1122">
              <div className="text-gray-600" data-id="element-1123">Critical SKUs</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1124">2</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default WarrantyClaimsDashboard;